<template>
<div class="chapter">
<div class="inner_page">
<div id="Chapter-Four.html" xml:lang="en-US">
<div class="Basic-Text-Frame-1">
<p class="ch"><a id="Anchor-1"></a>4</p>
<p class="body-text-opening"><span class="caps-epub" xml:lang="en-US">That day after
					</span>school, Lucy installed herself in the front parlor to wait for her father
					to come home.</p>
<p class="body-text">She was tired of her parents trying to protect her from things
					they thought she was too young to understand. Lucy supposed that had been all
					right when her world was limited to the bright-colored illustrations in her
					picture books, the elaborate tea parties she held for her dolls and stuffed
					toys, the swings and the slide at the playground in Rosecrans Park.</p>
<p class="body-text">But she was in the eighth grade now, and her world had been
					growing steadily for a long time. She’d read all the books in her classroom and
					begun on the ones on her parents’ shelves—the ones in English, anyway, most of
					which belonged to her mother. Some were a little melodramatic for her taste, but
					Lucy preferred to be bored and occasionally confused by Edna Ferber and Daphne
					du Maurier than by <em class="italic">Madeline</em> and <em class="italic">Caddie Woodlawn</em>.</p>
<p class="body-text">Consulting her mother about the future was out of the question.
					Miyako Takeda wasn’t like other mothers: she was quieter, prone to spells and
					moods. Withdrawn much of the time. Easily upset. And, of course, far more
					beautiful, which only made her seem more delicate, somehow.</p>
<p class="body-text">Renjiro Takeda, on the other hand, would know what to do. He
					was a businessman, well respected, important. Lucy pretended to read—a book
					called <em class="italic">The Rains Came</em> that had been made into a
					movie that she was too young to see, in which a lot of people appeared to be
					falling in love with each other. The book was so confusing that she didn’t
					intend to finish it, but it was as good as any, since she had too many things on
					her mind to pay attention to the words.</p>
<p class="body-text">At last, when dark had fallen and Lucy could hear her mother
					moving about the kitchen getting dinner ready, the front door opened. Her
					father’s face lit up when he spotted Lucy reading in the wing chair, but his
					smile didn’t disguise his weariness. He had been looking tired much of the time
					lately.</p>
<p class="body-text">“Hello, little one,” he said, removing his hat and placing it
					on a high peg of the coatrack. He was a natty dresser and his hat was made of
					fine wool, smooth to the touch, its edges turned up slightly. Next he hung his
					topcoat, brushing invisible specks off its tight-woven surface. Lucy liked to
					watch this ritual, and she waited patiently until he finished. Only then did he
					turn to her and hold his hands out. Lucy leapt off the chair and put her hands
					in his, and he swung her gently around, something she suspected she was too old
					for, but couldn’t bear to give up yet.</p>
<p class="body-text">“I have something for you,” he said.</p>
<p class="body-text">“What, Papa?”</p>
<p class="body-text">Her father pulled a small package wrapped in shiny white paper
					from his pocket. Lucy unfolded it carefully, revealing a mound of sugared
					almonds. Sometimes he brought candied lemon peel or crystallized ginger. He
					owned a business packing and shipping dried apricots, and he purchased treats
					for Lucy and her mother from the merchants and ranchers who brought their goods
					to the bustling business district.</p>
<p class="body-text">“Don’t eat them now.” Her father’s voice was teasing. “You’ll
					have no appetite for dinner and then Mother will be angry with me.”</p>
<p class="body-text">“Thank you, Papa.” Lucy carefully rewrapped the package. Then
					she took a breath. She had to talk to him now, when her mother wasn’t listening.
					“Something happened today in school.”</p>
<p class="body-text">He laid a heavy hand on her shoulder. “You discovered you are
					actually a princess?” he pretended to guess, wiggling his eyebrows. “With a
					crown and a kingdom to rule?”</p>
<p class="body-text">When Lucy was younger, her father would tell fantastical
					stories of apricots delivered by teams of white horses pulling wagons with
					silver fittings that sparkled in the sun, apricots so plump and perfect that
					each had a single green leaf attached to its stem, and he had to hire a pretty
					lady just to pluck the leaves and drop them into a basket, all day long. Lucy
					pretended to believe her father’s stories long after she understood that they
					were invented. She knew they pleased her mother. More precisely, Lucy knew that
					her own happiness pleased her mother, that the tableau they made, the three of
					them, prosperous and modern in their kitchen with its sleek metal cabinets and
					green tiles, was an achievement Miyako could never bring herself entirely to
					believe in.</p>
<p class="body-text">Already her father was moving toward the hall. Lucy knew he was
					anxious to greet her mother; he kissed her each evening as carefully as if she
					were made of spun sugar, and the smile he gave her was different from the one he
					had for Lucy. It was almost shy, if a father could ever be said to be shy.
					Usually, Lucy liked watching her father kiss her mother, but tonight she had to
					talk to him first.</p>
<p class="body-text">“Papa, be serious. I want to ask you about something. About the
					war.”</p>
<p class="body-text">That got his attention. Renjiro Takeda’s shoulders went rigid,
					and he turned slowly to face his daughter. His skin was stretched tight across
					his face; the lines around the corners of his mouth and under his eyes looked
					even deeper. “There is no war,” he said quietly. “Not in America.”</p>
<p class="body-text">“But there’s going to be.”</p>
<p class="body-text">“Who told you that?” His voice hardened, and Lucy was afraid.
					Not of her father—he was never angry with her, he was always kind—but of what
					the shift in his mood signified. “Who have you been talking to?”</p>
<p class="body-text">“Nobody. I mean, the kids at school talk.”</p>
<p class="body-text">“President Roosevelt will keep us out of the war. You don’t
					need to worry.” But he didn’t sound as certain as Lucy would have liked.</p>
<p class="body-text">“But Papa...I was supposed to be lunch monitor today.”</p>
<p class="body-text">Her mother’s steps echoed in the hall; she was coming to see
					what the delay was. Lucy put a hand on her father’s arm and spoke quickly,
					lowering her voice. “Papa, I was supposed to be lunch monitor but Nancy was
					instead, and Yvonne said it’s because I’m a Jap and her father says things are
					changing and—”</p>
<p class="body-text">But her father was cupping a hand to his ear and frowning, and
					she knew he was about to tell her to slow down and not talk so fast, to speak up
					so he could hear her. He was becoming hard of hearing; her mother teased him
					about it and threatened to buy him one of the new Dictograph hearing aids that
					were advertised on the radio.</p>
<p class="body-text">“Dinner’s almost ready!” her mother said, sweeping into the
					room. She’d touched up her lipstick as she always did before Renjiro came home,
					a slash of stark red against her fine, pale skin. “I made marble cake. And
					there’s ham.”</p>
<p class="body-text">Lucy watched her father’s expression change; neither of them
					had missed the faint edge to Miyako’s voice, the fact that her smile was a
					little too brittle and her words a little too breathless. But the biggest
					giveaway was the cooking. Miyako was a good cook, but she rarely had the energy
					for more than a cursory effort. She was having one of <em class="italic">those</em> days, and Renjiro’s outward calm faltered before he recovered
					and went to kiss his wife.</p>
<p class="body-text">Many afternoons when Lucy came home and let herself into the
					house with the key she wore on a chain around her neck, her mother would be
					lying down, her room darkened, the drapes closed. On her bedside table would be
					a glass of water and a folded cloth. Occasionally her mother would wet the cloth
					and drape it over her forehead. Lucy no longer went into her parents’ room on
					afternoons when the door was closed; her mother had asked her not to.</p>
<p class="body-text">“You’re thirteen,” she’d said shortly after Lucy’s birthday the
					prior year, before closing the bedroom door gently on Lucy’s face. “Old enough
					to take care of yourself for an hour or two while I rest.”</p>
<p class="body-text">But sometimes, every week or two, there would be a day when
					Miyako’s mood would swing in the other direction. She would have energy to
					spare. She cleaned and rearranged furniture, even though a lady came to clean
					every week. She tried new recipes and produced more courses than the three of
					them could eat. She met Renjiro at the door in her nicest apron and sat with him
					after dinner, talking breathlessly, her words chasing each other, instead of
					working on her embroidery by herself in the kitchen as she usually did. Nights
					like these were likely to end with the muffled sounds of Miyako crying in her
					bedroom, her father’s voice a smooth blanket, his words unintelligible through
					the wall their bedroom shared with Lucy’s. Long after they were finally silent,
					Lucy would lie awake in the dark, wondering what had made her mother so sad.</p>
<p class="body-text">She’d missed the signs today, so preoccupied was she with what
					had happened at school. Now she saw her opportunity slipping away, the chance to
					ask her father what to do about it. Renjiro was ever solicitous of Miyako, and
					Lucy knew—without jealousy, with calm acceptance—that she was the lesser planet
					in her father’s orbit.</p>
<p class="body-text">She felt more and more discouraged as they worked their way
					through her mother’s elaborate dinner. Miyako kept up a steady conversation, her
					sentences breaking off and starting over on entirely new subjects. She talked
					about a neighbor who had had something delivered in a large truck and a forecast
					she had heard on the radio that mentioned the possibility of hail and an article
					she’d read in a magazine about the first lady’s social secretary, and a dozen
					other things, too many to keep track of. Renjiro seemed even quieter than usual,
					answering in Japanese as often as he did in English, something he usually worked
					hard to avoid. Several times he set his fork down without eating the food he’d
					lifted halfway to his lips.</p>
<p class="body-text">After dinner, Lucy stayed in the kitchen, pretending to read
					again, as her mother cleaned up and her father fussed with the pipe that he
					smoked each night to help him digest his dinner, and finally her mother’s stream
					of words began to slow down, like a music box that would soon need to be wound
					again.</p>
<p class="body-text">Suddenly, a plate fell to the floor, causing Lucy to jump. In
					seconds, her mother was on her knees, and her voice broke as she scrambled for
					the fractured pieces.</p>
<p class="body-text">“I’m so clumsy, I can’t even hold a plate right—”</p>
<p class="body-text">“No, no, it’s all right, it’s nothing, let me help you.” Her
					father rose, setting his pipe down carefully. Then he paused, and slowly lowered
					himself back into his chair. “Oh. I’m sorry. Just a moment... Just give me a
					moment.”</p>
<p class="body-text">Lucy looked at him in alarm. His face looked grayish, his eyes
					wide and glassy. “Papa, are you all right?”</p>
<p class="body-text">“Yes, yes, of course, I’m just... Help your mother, <em class="italic">suzume</em>.”</p>
<p class="body-text">“Yes, Lucy, get the dustpan.”</p>
<p class="body-text">Lucy obeyed, breathing a sigh of relief. Her mother was fine;
					she had managed to bring herself back from the brink to which her mood had
					driven her, and her father was simply tired. He worked so hard at the factory,
					with all the employees for whom he was responsible, all the trucks bringing the
					apricots, the crates carrying them away, beautifully wrapped and packed and
					bearing her father’s name, all over the country. And she was her parents’ <em class="italic">suzume,</em> their little sparrow, and as she knelt to help
					pick up the broken pottery, she tried to hold on to the warm feeling that came
					from knowing that here in her home, she was the center of something.</p>
<p class="tb">* * *</p>
<p class="body-text-no-tab">The week passed slowly. Nancy took her place at the head
					of the class each day at noon, and Lucy pretended not to care. The boys on the
					playground found someone else to taunt. Miyako’s mood steadied, and when Lucy
					came home each day she found her mother embroidering in the parlor. She finished
					a rose-patterned scarf for her dresser and began a matching one for Lucy.</p>
<p class="body-text">On Saturday Renjiro wasn’t feeling well. The next morning, he
					stayed in his dressing gown to read the paper, and Miyako told Lucy that if she
					liked, she could go to church with the Koga family from down the street.</p>
<p class="body-text">Lucy welcomed the chance to sit in one of the pews up front
					between the young Koga children, her hands folded on her lap as she stole
					glances around the congregation, knowing she was being admired. Rarely did a
					week go by without someone stopping her family outside the church to tell her
					parents how beautiful and well-mannered Lucy was, how much she resembled Miyako.
					And Lucy knew that she would receive even more compliments than usual after she
					spent the service seated between the squirming Koga boys, helping their mother
					keep them quiet.</p>
<p class="body-text">She wore her navy coat with frog closures and her patent shoes
					and combed her hair until it shone. Lucy knew she was a beautiful girl, but for
					some reason this impressed adults even more than the other children in her
					class. Maybe it was because she had grown up with many of them, seeing each
					other every day. Now that she was fourteen, Lucy thought she could see signs of
					maturity in her face when she looked in her mother’s vanity mirror—a narrowing
					of her cheeks, an arch in her brow that more closely echoed her mother’s. Lucy
					wasn’t particularly vain, but she had observed her mother carefully enough to
					know that beauty was a tool that could be used to get all sorts of nice things.
					The best fish in the case at the market, say, or a seat on the trolley on days
					when it was crowded.</p>
<p class="body-text">As the reverend came to the end of one of his long and boring
					sermons and the congregation stood to sing the hymn, Lucy kept her eyes downcast
					as though she were praying. In reality, she was staring at Mrs. Koga’s brown
					pump, noting smugly how dowdy the plain, unadorned shoe was compared to the
					dressy high-heeled pairs in her mother’s closet. Lucy’s feet were still smaller
					than her mother’s, but soon they would be able to share—if she could convince
					Miyako that she was old enough for heels. By the age of fifteen, surely? These
					were the thoughts she was entertaining when the doors at the back of the church
					creaked open and two anxious figures burst inside, interrupting the listless
					singing of “Faith of Our Fathers.”</p>
<p class="body-text">Later she would remember the unfamiliar words repeated over and
					over by the adults all around her, <em class="italic">Pearl Harbor</em> and
						<em class="italic">torpedo</em> and <em class="italic">casualties</em>—but in the confusion inside the church, all Lucy could
					think about was that some unknown disaster had taken place and she was here,
					daydreaming, thinking selfish thoughts while her parents were over a mile away
					by themselves, her father ill and her mother barely able to take care of either
					of them. It was the first time Lucy understood that it would fall to her to help
					them if something bad had happened, the first time she realized that in some
					ways, her childhood was already far behind her.</p>
<p class="tb">* * *</p>
<p class="body-text-no-tab">Somehow, in the confusion following the news, Lucy ended
					up walking home alone. She imagined the Kogas realizing that she was missing,
					and feeling terrible about it—“How could we let that poor girl out of our
					sight?”—but even that was small comfort. She had a sense of foreboding, and
					though her shiny shoes pinched her toes, smashing them together, she hurried,
					almost running, her breath ragged in her lungs.</p>
<p class="body-text">When she turned down Clement Street and saw the ambulance in
					front of her house, she was horrified but not surprised. She’d known from the
					moment the strangers burst into the church that tragedy had come for her—that no
					matter what other cyclones of disaster had swept the world that morning, one was
					bearing down directly on Lucy. Everything that led up to this day had been a
					portent: her mother’s moods, the children’s cruelty, the glassy look in her
					father’s eyes—disaster.</p>
<p class="body-text">Two men emerged from her front door as she ran toward her
					house. Between them they carried a stretcher bearing a figure covered with a
					blanket. Behind them, a woman came out onto the porch, holding the door—Aiko
					Narita, her mother’s best friend.</p>
<p class="body-text">Lucy ran to the stretcher and threw herself upon it. Her
					father’s shoe jutted out underneath the blanket. If she could just get to him
					quickly enough, before they took him to the ambulance, there was a chance she
					could bring him back. If she touched his face, he might feel her hands and
					choose not to go. If she called his name, he might hear her, and understand that
					he couldn’t leave them behind, not like this.</p>
<p class="body-text">The two men didn’t see her coming, and they were startled. One
					of them said a bad word. Lucy’s fingers barely brushed the blanket when she was
					seized from behind and held in strong arms. She fought as hard as she could, but
					Auntie Aiko held her more tightly, and the men carried her father to the back of
					the ambulance, where the doors stood wide to receive them.</p>
<p class="body-text">“No, no, no, Lucy,” Aiko’s familiar voice crooned in her ear.
					Lucy kicked as hard as she could, connecting with Aiko’s shin; she tried to bite
					Aiko’s arm but couldn’t quite reach. She heard her own voice screaming, couldn’t
					get enough air. “It’s going to be all right,” Aiko gasped, struggling to contain
					her. “It’s going to be all right.”</p>
<p class="body-text">Auntie Aiko was a liar. Lucy knew that she wanted to help, but
					everything was wrong and Aiko wouldn’t let go, and she saw her chance slipping
					away as the doors to the ambulance closed and after a moment it started slowly
					down the street. Aiko tried to carry her back up the steps into the house, but
					Lucy twisted savagely and almost managed to slip away. Aiko caught the hem of
					her coat and dragged her back. The coat’s buttons popped off and went rolling
					down the sidewalk. One went over the curb, through the grate, and disappeared
					into the blackness below the street.</p>
<p class="body-text">One more loss, and finally Lucy gave up and allowed herself to
					be dragged, limp in Aiko’s arms. The button had been etched with the design of
					an anchor. They would never find another to match. The button would disappear in
					the muck and rotting leaves in the sewer, as the ambulance carrying her father’s
					body was disappearing out of view.</p>
<p class="Basic-Paragraph-1"></p>
</div>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// ChapterFour.vue?4163089a